import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { PMField } from "../../utils/PMField";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { dispatchWithToast, mapErrors } from "../../Utils";

import {
  ArrowRightIcon,
  CheckBadgeIcon,
  PencilIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { LoadingButton } from "@mui/lab";
import { ProjectsStackList } from "../projects/projects-stack-list";
import { ServiceAgreementsStackList } from "../service/agreements/agreements-stack-list";
import { ReportsStackList } from "../service/reports/reports-stack-list";
import { OrdersStacklist } from "../orders/orders-stack-list";
import { CasesStackList } from "../cases/cases-stack-list";
import { TWclassNames } from "../../utils/Div";
import { PMDialog } from "../../utils/PMDialog";
import { fetchCustomerContact } from "../../../api/customer";
import PMSwitch from "../../utils/PMSwitch";
import { CustomerSelector } from "../../utils/moduleSelectors/customerSelector";
import { DepartmentSelector } from "../../utils/moduleSelectors/departmentSelector";
import {
  createContactAsync,
  updateContactAsync,
} from "../../../slices/customer/contact";
export default function ContactDialog(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [itemState, setItemState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    contact: {},
  });

  // Deconstructing props
  const { open, onClose, selected, onRefresh, redirect } = props;

  // Deconstruct itemState (Store)
  const { contact, isLoading, error, errors } = itemState;

  const [readOnly, setReadOnly] = useState(props.readOnly);

  const getCustomer = async () => {
    return fetchCustomerContact(selected.id)
      .then((res) => {
        if (res?.data?.id) {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            contact: res?.data,
          }));
        }
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err?.data?.errors);
        } else {
          errors = ["Something went wrong"];
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          contact: {},
          error: true,
          errors: errors,
        }));
      });
  };

  useEffect(() => {
    if (open) {
      if (selected?.id) {
        getCustomer();
      } else {
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          contact: {},
          error: false,
          errors: [],
        }));

        setReadOnly(false);
      }
    }
  }, [dispatch, open, selected]);

  const createOrUpdateDispatch = (data) => {
    if (contact?.id) {
      const res = dispatch(updateContactAsync(data));
      return res;
    } else {
      const res = dispatch(createContactAsync(data));
      return res;
    }
  };

  const redirectToCustomer = (data) => {
    if (data?.id) {
      navigate(`/dashboard/contacts/` + data?.id);
    }
  };

  const CustomerForm = () => {
    return (
      <Formik
        enableReinitialize
        initialValues={{
          id: contact?.id,
          name: contact?.name || "",
          email: contact?.email || "",
          mobile: contact?.mobile || "",
          active: contact?.active ?? true,
          customer_id: contact?.customer?.id || null,
          department_id: contact?.department?.id || null,
          submit: null,
        }}
        validationSchema={Yup.object({
          name: Yup.string().required(t("Name is required")),
          email: Yup.string().email().required(t("Email is required")),
        })}
        onSubmit={(values, helpers) => {
          try {
            dispatchWithToast(
              createOrUpdateDispatch(values),
              redirect ? redirectToCustomer : onRefresh,
              onClose
            );
          } catch (err) {
            toast.error("Something went wrong...");
            helpers.setStatus({ success: false });
            helpers.setErrors({ submit: err.message });
            // helpers.setSubmitting(false);
          }
        }}
      >
        {(formik) => (
          <Form>
            <div className="grid grid-cols-6 gap-6 mt-10">
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="name"
                  type="text"
                  label={t("contact.name")}
                  readOnly={readOnly}
                  {...formik}
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="mobile"
                  type="text"
                  label={t("contact.mobile")}
                  readOnly={readOnly}
                  {...formik}
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="email"
                  type="text"
                  label={t("contact.email")}
                  readOnly={readOnly}
                  {...formik}
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <PMSwitch label={t("Active")} name="active" {...formik} />
              </div>
            </div>

            <div className="mt-10">
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-3">
                  <CustomerSelector name="customer_id" label={t("Customer")} {...formik} />
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <DepartmentSelector name="department_id" label={t("Department")} {...formik} />
                </div>
              </div>
            </div>

            <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
              <button
                type="button"
                className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                onClick={onClose}
              >
                {t("Close")}{" "}
              </button>

              {readOnly ? null : (
                <LoadingButton
                  loading={formik.isSubmitting}
                  disabled={isLoading}
                  loadingPosition="start"
                  startIcon={<CheckBadgeIcon />}
                  type="button"
                  variant="contained"
                  color={"success"}
                  onClick={formik.submitForm}
                >
                  {contact.id ? t("Update") : t("Create")}
                </LoadingButton>
              )}
            </div>
          </Form>
        )}
      </Formik>
    );
  };

  const [selectedTab, setSelectedTab] = useState(0);

  const handleRefresh = (data) => {
    setItemState((prevState) => ({
      ...prevState,
      contact: data,
      isLoading: false,
    }));
  };

  const handleLoading = () => {
    setItemState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));
  };

  const tabs = [
    {
      name: "Detaljer",
      component: (
        <CustomerForm
          item={contact}
          onRefresh={(data) => handleRefresh(data)}
          onLoading={() => handleLoading()}
        />
      ),
    },
    {
      name: "Cases",
      component: (
        <CasesStackList
          module_param={contact.id}
          module="contact"
          filters={[
            {
              name: "contact",
              value: contact.id,
            },
          ]}
        />
      ),
    },
    {
      name: "Ordrer",
      component: (
        <OrdersStacklist
          module_param={contact.id}
          module="contact"
          filters={[
            {
              name: "contact",
              value: contact.id,
            },
          ]}
        />
      ),
    },
    {
      name: "Reports",
      component: (
        <ReportsStackList
          module_param={contact.id}
          module="contact"
          filters={[
            {
              name: "contact",
              value: contact.id,
            },
          ]}
        />
      ),
    },
    {
      name: "Agreements",
      component: (
        <ServiceAgreementsStackList
          module_param={contact.id}
          module="contact"
          filters={[
            {
              name: "contact",
              value: contact.id,
            },
          ]}
        />
      ),
    },
    {
      name: "Projects",
      href: "#",
      component: (
        <ProjectsStackList
          module_param={contact.id}
          module="contact"
          filters={[
            {
              name: "contact",
              value: contact.id,
            },
          ]}
        />
      ),
    },
  ];

  function contactTabs() {
    return (
      <div>
        <div className="sm:hidden">
          <label htmlFor="tabs" className="sr-only">
            Select a tab
          </label>
          {/* Use an "onChange" listener to redirect the user to the selected tab URL. */}
          <select
            onChange={(e) => setSelectedTab(e.target.value)}
            id="tabs"
            name="tabs"
            className="block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
            defaultValue={tabs[selectedTab].name}
          >
            {tabs.map((tab, tabIdx) => (
              <option key={tab.name} value={tabIdx}>
                {tab.name}
              </option>
            ))}
          </select>
        </div>
        <div className="hidden sm:block">
          <nav
            className="isolate flex divide-x divide-gray-200 rounded-lg shadow"
            aria-label="Tabs"
          >
            {tabs.map((tab, tabIdx) => (
              <a
                key={tab.name}
                onClick={() => setSelectedTab(tabIdx)}
                className={TWclassNames(
                  tabIdx === selectedTab
                    ? "text-gray-900"
                    : "text-gray-500 hover:text-gray-700",
                  tabIdx === 0 ? "rounded-l-lg" : "",
                  tabIdx === tabs.length - 1 ? "rounded-r-lg" : "",
                  "cursor-pointer group relative min-w-0 flex-1 overflow-hidden bg-white py-4 px-4 text-center text-sm font-medium hover:bg-gray-50 focus:z-10"
                )}
                aria-current={tabIdx === selectedTab ? "page" : undefined}
              >
                <span>{tab.name}</span>
                <span
                  aria-hidden="true"
                  className={TWclassNames(
                    tabIdx === selectedTab ? "bg-indigo-500" : "bg-transparent",
                    "absolute inset-x-0 bottom-0 h-0.5"
                  )}
                />
              </a>
            ))}
          </nav>
        </div>
      </div>
    );
  }

  const modalHeaderActions = [
    {
      label: "Gå til kunde",
      icon: ArrowRightIcon,
      path: `/dashboard/contacts/` + contact?.id,
    },
    {
      label: readOnly ? t("edit") : t("read_only"),
      icon: readOnly ? XMarkIcon : PencilIcon,
      action: () => setReadOnly(!readOnly),
    },
  ];

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      headerActions={modalHeaderActions}
      isLoading={isLoading}
      title={
        readOnly
          ? contact?.id
            ? contact?.displayTitle
            : t("contact")
          : contact?.id
          ? contact?.displayTitle
          : t("contacts.create")
      }
    >
      <>
        {contactTabs()}
        <div className="py-5">{tabs[selectedTab].component}</div>
      </>
    </PMDialog>
  );
}

ContactDialog.defaultProps = {
  selected: null,
  // Read only is used when only wanting to display info. Update or creating will not work.
  readOnly: false,
  open: false,
  selection: false,
  onClose: () => toast.error("Missing onClose function. Cannot close dialog"),
  redirect: false,
};
